import { Navigate, useParams } from 'react-router-dom'
import { Layout } from '../components/Layout'
import {
  firstNodeId,
  firstUnansweredNodeId,
  nodeById,
  type LandingNode,
  type QuestionNode,
  type ValueNode,
} from '../funnel/flow'
import { routeFor, useFunnel } from '../funnel/useFunnel'
import { CraftingScreen } from './CraftingScreen'
import { EmailGateScreen } from './EmailGateScreen'
import { HandoffScreen } from './HandoffScreen'
import { InsightScreen } from './InsightScreen'
import { LandingScreen } from './LandingScreen'
import { QuestionScreen } from './QuestionScreen'
import { ResultScreen } from './ResultScreen'
import { OfferScreen } from './OfferScreen'
import { ValueScreen } from './ValueScreen'
import { WaitlistScreen } from './WaitlistScreen'

/** Screens past the questions have no progress bar and no back button. */
const TERMINAL_KINDS = new Set(['landing', 'crafting', 'gate', 'result', 'confirm', 'offer', 'handoff'])

/**
 * Single dispatcher for every funnel screen. The route carries a node id and the
 * node's `kind` decides what renders — so adding a screen means adding a node to
 * the graph, not a route to the router.
 */
export function NodeScreen({ nodeId: fixedId }: { nodeId?: string }) {
  const params = useParams<{ nodeId: string }>()
  const funnel = useFunnel()
  const nodeId = fixedId ?? params.nodeId ?? firstNodeId
  const node = nodeById(nodeId)

  // Unknown id (stale bookmark, hand-typed URL): send them to wherever their
  // answers actually left off rather than restarting or dead-ending.
  if (!node) return <Navigate to={routeFor(firstUnansweredNodeId(funnel.answers, funnel.flags))} replace />

  const isLanding = node.id === firstNodeId
  const terminal = TERMINAL_KINDS.has(node.kind)
  // First real question — the one that decides the branch.
  const isFirstQuestion = node.id === 'connection_context'

  const body = (() => {
    switch (node.kind) {
      case 'landing':
        return <LandingScreen node={node as LandingNode} />
      case 'question':
        return <QuestionScreen node={node as QuestionNode} isFirst={isFirstQuestion} />
      case 'insight':
        return <InsightScreen node={node as ValueNode} />
      case 'value':
      case 'howitworks':
        return <ValueScreen node={node as ValueNode} />
      case 'crafting':
        return <CraftingScreen />
      case 'gate':
        return <EmailGateScreen />
      case 'result':
        return <ResultScreen />
      case 'confirm':
        return <WaitlistScreen />
      case 'offer':
        return <OfferScreen />
      case 'handoff':
        return <HandoffScreen />
    }
  })()

  return (
    <Layout
      // No progress bar on the landing: showing "you're 8% done" before the
      // visitor has agreed to anything reads as a commitment they didn't make.
      progress={terminal || isLanding ? undefined : funnel.progress(node.id)}
      showBack={!isLanding && !terminal}
      onBack={() => funnel.goBack(node.id)}
    >
      {body}
    </Layout>
  )
}
